/**
 * SpeedtestsTab — W1.3 / B1.17-Anbindung
 *
 * Zeigt Speedtest-Einträge vom Backend.
 * Das Feld `server` enthält die Geräte-Serial — Label im UI: "Gerät".
 */
import { useTranslation } from 'react-i18next'
import { useHotspotSpeedtests } from './useHotspotSpeedtests'
import { fmt } from '../../lib/fmt'
import { activeBcp47 } from '../../lib/locale'

/**
 * Fix #129: `taken_at` kam als roher ISO-Zeitstempel ins UI, die Mess-Werte
 * mit bis zu 6 Nachkommastellen direkt vom Backend. Formatierung folgt dem
 * bestehenden Muster aus HotspotOnlineStatusCard/HotspotGuardToggle
 * (`toLocaleString(activeBcp47(), {...})`) statt neuer Logik.
 */
function formatTakenAt(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(activeBcp47(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type Props = {
  hotspotId: string
}

export function SpeedtestsTab({ hotspotId }: Props) {
  const { t } = useTranslation('hotspots')
  const { data, isLoading } = useHotspotSpeedtests(hotspotId)

  return (
    <div className="speedtests-tab">
      {/* Hinweis-Banner — immer sichtbar */}
      <div className="hint" style={{ marginBottom: 14 }}>
        {t('speedtests.hint')}
      </div>

      {/* Loading-State */}
      {isLoading && (
        <div className="skeleton" style={{ height: 80, borderRadius: 8 }} />
      )}

      {/* Empty-State */}
      {!isLoading && (!data || data.length === 0) && (
        <div
          className="card card-pad"
          style={{ textAlign: 'center', color: 'var(--text-muted)' }}
        >
          {t('speedtests.emptyMsg')}
        </div>
      )}

      {/* Datenliste */}
      {!isLoading &&
        data &&
        data.map((entry) => (
          <div
            key={entry.id}
            className="list-row"
            style={{
              gridTemplateColumns: 'minmax(220px,1fr) 1fr 1fr 1fr auto',
            }}
          >
            <div className="identity">
              <div className="identity-meta">
                <div className="identity-sub">{formatTakenAt(entry.taken_at)}</div>
                {entry.server && (
                  <div className="identity-sub muted">{t('speedtests.labelDevice')}: {entry.server}</div>
                )}
              </div>
            </div>
            <div>
              <div className="muted">{t('speedtests.labelDown')}</div>
              <div className="num-md">
                {entry.down_mbps == null ? '—' : fmt(entry.down_mbps, 1)} <span>Mb/s</span>
              </div>
            </div>
            <div>
              <div className="muted">{t('speedtests.labelUp')}</div>
              <div className="num-md">
                {entry.up_mbps == null ? '—' : fmt(entry.up_mbps, 1)} <span>Mb/s</span>
              </div>
            </div>
            <div>
              <div className="muted">{t('speedtests.labelPing')}</div>
              <div className="num-md">
                {/* Nachbesserung #129: `speedtests.latency` ist im Backend eine
                    Integer-Spalte (db/schema.rb) — 1 Nachkommastelle würde
                    Genauigkeit vortäuschen, die die Daten nicht haben. */}
                {entry.ping_ms == null ? '—' : fmt(entry.ping_ms, 0)} <span>ms</span>
              </div>
            </div>
          </div>
        ))}
    </div>
  )
}
